import type { CityConfig } from "@/pages/city-template/types";

export const showLowConfig: CityConfig = {
  city: "Show Low",
  state: "Arizona",
  stateAbbr: "AZ",
  slug: "show-low",
  route: "/cities/show-low",
  title: "Emergency & Expedited Passport Services Show Low AZ | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Show Low, AZ — as fast as 24 hours. No Show Low passport agency. Fast Passport Center couriers to the Western Passport Center in Tucson, about 200 miles south. A+ BBB rated. Serving White Mountains families, Pinetop-Lakeside travelers, Summit Healthcare professionals, and Navajo County residents.",
  h1: "Emergency & Expedited Passport Services in Show Low, AZ",
  heroSubtitle: "Show Low sits in the heart of Arizona's White Mountains at 6,300 feet elevation, about 200 miles from the Western Passport Center in Tucson. Fast Passport Center's registered couriers deliver your documents to Tucson so you get a new or renewed passport as fast as 24 hours — whether you're a White Mountains family planning an international vacation, a Summit Healthcare professional attending medical conferences abroad, or a Navajo County resident who would rather not drive 400 miles round-trip through mountainous terrain to Tucson.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Show%20Low%20Arizona%20White%20Mountains%20landscape%20with%20ponderosa%20pine%20forest%20stretching%20across%20rolling%20mountain%20terrain%20under%20bright%20blue%20sky%20with%20dramatic%20cumulus%20clouds%2C%20high%20elevation%20mountain%20town%20nestled%20in%20pine%20trees%2C%20golden%20afternoon%20light%20filtering%20through%20forest%20canopy%2C%20professional%20landscape%20photography%2C%20clean%20composition%2C%20no%20people%20visible&width=1800&height=700&seq=sl2-hero-001&orientation=landscape",
  heroImageAlt: "Show Low Arizona White Mountains ponderosa pine landscape at golden hour",
  geoPosition: "34.2542;-110.0298",
  geoPlacename: "Show Low, Arizona",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Western Passport Center",
    address: "733 N Stone Ave, Tucson, AZ 85701",
    distance: "Approximately 200 miles from Show Low — about 3.5 hours via AZ-77 and I-10",
  },
  nearbyCitiesZips: [
    { name: "Show Low", zips: "85901, 85902" },
    { name: "Pinetop-Lakeside", zips: "85929, 85935" },
    { name: "Snowflake", zips: "85937" },
    { name: "Taylor", zips: "85939" },
    { name: "Heber-Overgaard", zips: "85933" },
    { name: "Eagar", zips: "85925" },
    { name: "Springerville", zips: "85938" },
    { name: "St. Johns", zips: "85936" },
    { name: "Whiteriver", zips: "85941" },
    { name: "Holbrook", zips: "86025" },
    { name: "Winslow", zips: "86047" },
    { name: "Concho", zips: "85924" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Western Passport Center in Tucson — 200 miles from Show Low" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "White Mountains Hub", desc: "Serving Show Low, Pinetop-Lakeside, Snowflake, Taylor, and all Navajo and Apache County mountain communities" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Show Low case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Holbrook AZ", route: "/cities/holbrook" },
    { name: "Expedited passport in Phoenix AZ", route: "/cities/phoenix" },
    { name: "Urgent passport in Tucson AZ", route: "/cities/tucson" },
    { name: "Arizona passport hub", route: "/states/arizona" },
    { name: "Fast passport in Winslow AZ", route: "/cities/winslow" },
    { name: "Emergency passport in Scottsdale AZ", route: "/cities/scottsdale" },
    { name: "Expedited passport in Flagstaff AZ", route: "/cities/flagstaff" },
  ],
  fpcOfficeAddress: "733 N Stone Ave, Tucson, AZ 85701",
  fpcOfficeNote: "Fast Passport Center serves Show Low remotely via secure courier. Our registered couriers hand-deliver your documents to the Western Passport Center in Tucson. Show Low is approximately 200 miles from Tucson through mountainous terrain — about a 7-hour round trip that is completely unnecessary with our courier service. For White Mountains residents, this is the fastest and most practical way to get a passport without leaving the mountain.",
  fpcOfficeDistance: "Serving all of Navajo County and the White Mountains region",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Show Low, Pinetop-Lakeside, Snowflake, Taylor, Heber-Overgaard, and the entire White Mountains region. From Summit Healthcare physicians attending international medical conferences to Navajo County families taking kids on their first international trip, we help White Mountains residents get passports fast without the 400-mile Tucson round trip.",
  nearbyCitiesIntro: "We serve Show Low and nearby communities including Pinetop-Lakeside, Snowflake, Taylor, Heber-Overgaard, Eagar, Springerville, and the entire White Mountains region — from the Mogollon Rim to the Apache-Sitgreaves National Forests.",
  howItWorksIntro: "Most Show Low travelers complete everything online and ship documents overnight. At about 200 miles from Tucson through mountain highways, Fast Passport Center provides essential courier turnaround for White Mountains residents — a critical service for one of Arizona's most scenic and remote passport communities.",
  mistakesIntro: "Show Low travelers, including White Mountains families and Summit Healthcare professionals, face unique passport challenges specific to high-elevation mountain communities. Here are the most common mistakes we see.",
  testimonialsIntro: "Show Low and White Mountains travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Show Low? Here are the most common ones we hear from White Mountains residents.",
  statePageRoute: "/states/arizona",
  statePageName: "Arizona",
  dmvWebsite: "https://azdot.gov/motor-vehicles",
  dmvLabel: "Arizona MVD — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "200 miles to Tucson (3.5-hour drive each way through mountains)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No 7-hour round-trip mountain drive to Tucson needed",
        "Ideal for White Mountains families getting first passports",
        "Perfect for Summit Healthcare international conference travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from SOW or PHX",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Show Low is 200 miles from Tucson through mountain highways. Don't waste a 7-hour round trip — our couriers deliver to the Western Passport Center for you.",
      bullets: ["White Mountains coverage", "24–48 hour turnaround available", "No 7-hour Tucson mountain drive needed"],
      badge: "Fastest in AZ",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Show Low adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Show Low and Navajo County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Show Low and Navajo County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Show Low, Pinetop-Lakeside, and White Mountains residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Tucson agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Navajo County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Show Low and White Mountains residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Show Low AZ", description: "Urgent passport processing for Show Low, AZ travelers via Western Passport Center courier — about 200 miles from Show Low." },
    { name: "Passport Renewal Show Low AZ", description: "Fast passport renewal for Show Low and Navajo County, Arizona residents." },
    { name: "New Passport Show Low AZ", description: "First-time passport application assistance for Show Low and White Mountains region residents." },
    { name: "Lost Passport Show Low AZ", description: "Passport replacement for lost or stolen passports for Show Low and Navajo County residents." },
    { name: "Child Passport Show Low AZ", description: "Minor passport services for Show Low and White Mountains families." },
    { name: "Name Change Passport Show Low AZ", description: "Passport name change service for Show Low, AZ residents." },
  ],
  testimonials: [
    {
      name: "Dr. Mark T.",
      location: "Show Low, AZ",
      rating: 5,
      text: "Cardiologist at Summit Healthcare. Got invited to present at an international cardiology conference in Barcelona with only 3 weeks notice. My passport had expired and I needed it fast. Fast Passport Center got me a new one in 4 business days. Didn't have to cancel my presentation — the courier service is a lifesaver for mountain town professionals.",
      service: "Expedited Renewal",
    },
    {
      name: "The Richardson Family",
      location: "Pinetop-Lakeside, AZ",
      rating: 5,
      text: "Family of five planning our first international trip to Cancun. Three kids needed first passports, plus my wife and I needed renewals. Fast Passport Center walked us through the whole DS-11 process for the kids. All five passports arrived in 7 business days. The kids are so excited — we couldn't have done this through the Show Low post office in time.",
      service: "Family Passport Package",
    },
    {
      name: "Sarah J.",
      location: "Show Low, AZ",
      rating: 5,
      text: "I'm a teacher at Show Low High School leading a student trip to Costa Rica over spring break. Realized my passport was expired 2 weeks before departure. Fast Passport Center got me a renewal in 5 business days. The daily email updates kept me sane. Trip went off perfectly.",
      service: "Expedited Renewal",
    },
    {
      name: "Kevin L.",
      location: "Snowflake, AZ",
      rating: 5,
      text: "My company sent me to a training in Tokyo with 10 days notice. I'd never had a passport before. Fast Passport Center guided me through the whole first-time application process and got my passport in 6 business days. The dedicated agent — he was incredible. He told me exactly where to go for my acceptance clerk visit and what to bring.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "sl2-1",
      name: "Show Low Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "191 W Deuce of Clubs",
      city: "Show Low",
      state: "AZ",
      zip: "85901",
      phone: "(928) 537-5787",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Show Low",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Show Low and White Mountains residents. Conveniently located on Deuce of Clubs. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "sl2-2",
      name: "Pinetop Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "712 E White Mountain Blvd",
      city: "Pinetop",
      state: "AZ",
      zip: "85935",
      phone: "(928) 367-2258",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles south in Pinetop",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Pinetop-Lakeside and southern Show Low residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "sl2-3",
      name: "Snowflake Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "57 S 1st St W",
      city: "Snowflake",
      state: "AZ",
      zip: "85937",
      phone: "(928) 536-7413",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "20 miles northeast in Snowflake",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Snowflake, Taylor, and northern Navajo County residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "sl2-4",
      name: "Holbrook Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 W Erie St",
      city: "Holbrook",
      state: "AZ",
      zip: "86025",
      phone: "(928) 524-3311",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "55 miles northeast in Holbrook",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative acceptance facility for northeastern Navajo County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "sl2-5",
      name: "Navajo County Superior Court — Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "100 E Code Talkers Dr",
      city: "Holbrook",
      state: "AZ",
      zip: "86025",
      phone: "(928) 524-4188",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM (passport services by appointment only)",
      distance: "55 miles northeast in Holbrook",
      website: "https://www.navajocountyaz.gov/Departments/Clerk-of-the-Superior-Court",
      websiteLabel: "Navajo County Clerk",
      notes: "County clerk passport acceptance by appointment. Call ahead to schedule and confirm availability.",
      icon: "ri-building-line",
    },
    {
      id: "sl2-6",
      name: "Springerville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "120 N Papago St",
      city: "Springerville",
      state: "AZ",
      zip: "85938",
      phone: "(928) 333-4966",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "40 miles east in Springerville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Eagar, Springerville, and eastern Apache County residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Tucson Agency", agency: "200 miles — 3.5-hr drive each way through mountains", fpc: "We drive for you — no 7-hour mountain round trip needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Thinking You Can Drive to Tucson in a Day",
      description: "Show Low is 200 miles from Tucson through AZ-77 mountain highways and I-10 desert stretches — a 7-hour round trip. That is an entire day spent driving just to reach the Western Passport Center — and you still need an appointment. Our courier service eliminates this entirely. Stay in the White Mountains and let us handle Tucson for you.",
    },
    {
      icon: "ri-snowy-line",
      title: "Winter Travel and Passport Planning — The White Mountains Weather Trap",
      description: "Show Low sits at 6,300 feet and regularly gets significant snow from November through March. Travelers planning winter international trips often wait too long to apply, then discover that winter storms have delayed mail processing. Start your passport application 2–3 months before planned winter international travel — or use our fast courier service for tight deadlines.",
    },
    {
      icon: "ri-image-line",
      title: "Rural Photo Quality Issues",
      description: "Passport photo services in the White Mountains are limited. Passport photos must be exactly 2x2 inches with a white background, no shadows, and neutral expression. Big-box store photo centers in Show Low can handle this, but always verify your photos meet U.S. Department of State standards before submitting. Improper photos are the number one cause of application rejection.",
    },
    {
      icon: "ri-calendar-line",
      title: "Seasonal Acceptance Facility Availability",
      description: "Passport acceptance facilities in the White Mountains have limited hours — typically 9 AM to 4 PM on weekdays only. During summer tourist season, appointments can fill up 2–3 weeks in advance. Schedule your acceptance facility visit well before your planned travel date, especially if you need a first-time passport requiring an in-person appearance.",
    },
    {
      icon: "ri-file-list-line",
      title: "First-Time Applicants Missing Documentation",
      description: "Many White Mountains residents applying for their first passport arrive at acceptance facilities without certified birth certificates — not photocopies, not hospital certificates. First-time applicants need a certified birth certificate with a raised seal, a valid photo ID, and a completed DS-11 form. Securing these documents can take weeks if you need to order from out of state.",
    },
    {
      icon: "ri-bus-line",
      title: "School and Youth Group Travel — Document Timing",
      description: "Show Low High School and Blue Ridge High School student travel programs to Mexico, Costa Rica, and Europe require valid passports. Parents often underestimate the processing timeline and apply too late. Minor passport applications require both parents to appear in person — coordinate parental availability well in advance of departure.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Show Low or Pinetop." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Western Passport Center in Tucson — 200 miles south via AZ-77." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Western Passport Center via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Show Low or White Mountains address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Show Low from a passport agency?",
      a: "Show Low is approximately 200 miles from the Western Passport Center in Tucson — about a 3.5-hour drive each way via AZ-77 and I-10 through mountainous terrain. That is a 7-hour round trip. Show Low is one of Arizona's most geographically remote communities from the state's only passport agency. Fast Passport Center's courier service makes the trip to Tucson so you don't have to.",
    },
    {
      q: "Does Show Low have its own passport acceptance facility?",
      a: "Yes. The Show Low Post Office at 191 W Deuce of Clubs is a USPS passport acceptance facility. The Pinetop Post Office at 712 E White Mountain Boulevard also offers acceptance services. Both require appointments and operate Monday through Friday during normal business hours.",
    },
    {
      q: "How fast can I get an emergency passport in Show Low?",
      a: "Emergency processing is typically 24–48 hours for Show Low residents. Our couriers deliver directly to the Western Passport Center in Tucson — 200 miles away. This saves you a 7-hour mountain round-trip drive. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Can I fly from Show Low Regional Airport internationally?",
      a: "Show Low Regional Airport (SOW) serves regional and general aviation routes, primarily connecting to Phoenix Sky Harbor (PHX). For international commercial flights, White Mountains residents typically fly from Phoenix Sky Harbor International Airport (PHX), about 175 miles southwest, or Albuquerque International Sunport (ABQ), about 220 miles east. Both require a valid passport book.",
    },
    {
      q: "Is winter weather a concern for passport processing in Show Low?",
      a: "Yes — Show Low at 6,300 feet receives heavy snowfall from November through March. Standard mail delivery can be delayed by winter storms. Fast Passport Center uses UPS overnight courier with full tracking, which is significantly more reliable than standard USPS during White Mountains winter months. We recommend starting your passport application process early if you are planning international travel during winter.",
    },
    {
      q: "I'm a Summit Healthcare professional — can I get my passport fast for an international medical conference?",
      a: "Absolutely. Fast Passport Center specializes in fast turnaround for medical professionals with short-notice international conference travel. Our 2–9 business day expedited service is ideal for physicians, nurses, and healthcare administrators at Summit Healthcare who receive conference invitations with limited lead time.",
    },
    {
      q: "How do I renew my passport in Show Low?",
      a: "Adult renewals (16 or older, issued within 15 years, same name) can be done entirely by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than standard USPS processing (8–11 weeks). This is especially valuable for Show Low residents given the 200-mile distance to Tucson.",
    },
    {
      q: "Where can I get passport photos in Show Low?",
      a: "The closest passport photo locations are at Walmart Supercenter at 5401 S White Mountain Rd in Show Low, and Walgreens at 21 S White Mountain Rd. UPS Store in Show Low also offers passport photos. Always verify your photos meet exact 2x2 inch specifications with a white background before submitting your application.",
    },
    {
      q: "Do you serve Pinetop-Lakeside and Snowflake residents too?",
      a: "Yes. We serve all of Navajo County and the White Mountains region including Pinetop-Lakeside, Snowflake, Taylor, Heber-Overgaard, Eagar, Springerville, and the entire mountain corridor. Documents ship overnight from any UPS location in the Show Low and Pinetop area.",
    },
    {
      q: "What if I need passports for my kids for a school trip?",
      a: "Show Low and Blue Ridge school districts frequently organize international student trips. Children under 16 need first-time passports requiring both parents to appear in person at an acceptance facility — or one parent with notarized Form DS-3053. Start the process at least 2–3 months before the trip, especially since acceptance facility appointments fill quickly during school break seasons.",
    },
    {
      q: "How does the Mogollon Rim and mountain terrain affect mail delivery?",
      a: "The White Mountains present unique logistics challenges. Mountain highways like AZ-260 and AZ-77 can be affected by weather year-round — winter snow, summer monsoon floods, and spring wildfire road closures. Fast Passport Center's UPS overnight courier with tracking and temperature-controlled handling ensures your documents arrive safely, even during challenging White Mountains conditions.",
    },
    {
      q: "I live in a rural area outside Show Low — can I still use your service?",
      a: "Yes. Our service is fully remote — you never need to visit our office. Complete your application online, visit an acceptance facility for your identity verification (if required), and we handle everything else. Documents are shipped via prepaid UPS overnight label from any UPS drop-off location in the White Mountains region.",
    },
  ],
  photoLocations: [
    { name: "Walmart Supercenter — Show Low", address: "5401 S White Mountain Rd, Show Low, AZ 85901", hours: "Mon–Sun: 8AM–8PM" },
    { name: "Walgreens — Show Low", address: "21 S White Mountain Rd, Show Low, AZ 85901", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Show Low", address: "4451 S White Mountain Rd, Ste B, Show Low, AZ 85901", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–3PM" },
    { name: "Walgreens — Pinetop", address: "20 E White Mountain Blvd, Pinetop, AZ 85935", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walmart Supercenter — Taylor", address: "2400 N Main St, Taylor, AZ 85939", hours: "Mon–Sun: 8AM–8PM" },
    { name: "Bashas' — Pinetop", address: "1751 W White Mountain Blvd, Pinetop, AZ 85935", hours: "Mon–Sun: 7AM–9PM" },
  ],
  dmvLocations: [
    { name: "AZ MVD — Show Low", address: "161 E Deuce of Clubs, Show Low, AZ 85901", hours: "Mon–Fri: 8:00AM–5:00PM", note: "REAL ID and driver's license renewals for Show Low and White Mountains residents. Right in town on Deuce of Clubs.", featured: true },
    { name: "AZ MVD — Holbrook", address: "2100 Navajo Blvd, Holbrook, AZ 86025", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves Holbrook, Joseph City, and northern Navajo County residents. About 55 miles northeast.", featured: false },
    { name: "AZ MVD — St. Johns", address: "201 E 2nd St N, St. Johns, AZ 85936", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves St. Johns and Apache County northeastern residents.", featured: false },
  ],
  airports: [
    {
      code: "SOW",
      name: "Show Low Regional Airport",
      distance: "2 miles east of downtown Show Low",
      address: "3200 Airport Loop, Show Low, AZ 85901",
      note: "Regional airport with scheduled service to Phoenix Sky Harbor via Southern Airways Express. Useful for connecting to international flights at PHX. Passport required for international connections.",
      flightUrl: "https://www.showlowaz.gov/271/Airport",
      flightLabel: "SOW Airport",
      featured: true,
    },
    {
      code: "PHX",
      name: "Phoenix Sky Harbor International Airport",
      distance: "175 miles southwest of Show Low",
      address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
      note: "Arizona's largest international airport with extensive domestic and global routes. About 3 hours via AZ-260 and AZ-87.",
      flightUrl: "https://www.skyharbor.com/",
      flightLabel: "PHX Airport",
      featured: false,
    },
    {
      code: "ABQ",
      name: "Albuquerque International Sunport",
      distance: "220 miles east of Show Low",
      address: "2200 Sunport Blvd SE, Albuquerque, NM 87106",
      note: "Major New Mexico international hub with domestic and international routes. About 4 hours east via US-60 and I-40. A viable alternative for White Mountains residents heading east.",
      flightUrl: "https://www.abqsunport.com/",
      flightLabel: "ABQ Airport",
      featured: false,
    },
    {
      code: "TUS",
      name: "Tucson International Airport",
      distance: "200 miles south of Show Low",
      address: "7250 S Tucson Blvd, Tucson, AZ 85756",
      note: "Southern Arizona international airport with domestic and limited international service. About 3.5 hours south via AZ-77.",
      flightUrl: "https://www.flytucson.com/",
      flightLabel: "TUS Airport",
      featured: false,
    },
  ],
};